import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Grid,
  Stack,
  Typography,
  Skeleton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import sh from "bundles/common/utils/sh";
import WorkIcon from "@mui/icons-material/Work";
import BusinessIcon from "@mui/icons-material/Business";
import AddIcon from "@mui/icons-material/Add";
import ApplyToOfferDialog from "bundles/Internal/components/common/ApplyToOfferDialog";
import UserAvatar from "bundles/Internal/components/common/UserAvatar";

const STAGE_PALETTES = [
  { bg: "hsla(210, 70%, 50%, 0.12)", color: "hsl(210 70% 35%)" },
  { bg: "hsla(150, 60%, 40%, 0.12)", color: "hsl(150 60% 30%)" },
  { bg: "hsla(30, 85%, 50%, 0.12)", color: "hsl(30 85% 35%)" },
  { bg: "hsla(260, 50%, 50%, 0.12)", color: "hsl(260 50% 35%)" },
  { bg: "hsla(340, 65%, 50%, 0.12)", color: "hsl(340 65% 35%)" },
  { bg: "hsla(180, 55%, 40%, 0.12)", color: "hsl(180 55% 30%)" },
  { bg: "hsla(80, 55%, 42%, 0.12)", color: "hsl(80 55% 30%)" },
  { bg: "hsla(0, 65%, 50%, 0.12)", color: "hsl(0 65% 38%)" },
];

const FIXED_STAGE_COLORS = {
  "en cours":        { bg: "hsla(210, 70%, 50%, 0.12)", color: "hsl(210 70% 35%)" },
  "in behandeling":  { bg: "hsla(210, 70%, 50%, 0.12)", color: "hsl(210 70% 35%)" },
  "en attente":      { bg: "hsla(30, 85%, 50%, 0.12)", color: "hsl(30 85% 35%)" },
  "in afwachting":   { bg: "hsla(30, 85%, 50%, 0.12)", color: "hsl(30 85% 35%)" },
  "recruté":         { bg: "hsla(150, 60%, 40%, 0.12)", color: "hsl(150 60% 30%)" },
  "aangenomen":      { bg: "hsla(150, 60%, 40%, 0.12)", color: "hsl(150 60% 30%)" },
  "closed":          { bg: "hsla(0, 65%, 50%, 0.12)", color: "hsl(0 65% 38%)" },
};

const stageColorCache = {};
let nextColorIdx = 0;

const getStageColor = (stage) => {
  if (!stage) return { bg: "hsla(0, 0%, 50%, 0.12)", color: "hsl(0 0% 40%)" };
  const key = stage.toLowerCase();
  if (FIXED_STAGE_COLORS[key]) return FIXED_STAGE_COLORS[key];
  if (!stageColorCache[key]) {
    stageColorCache[key] = STAGE_PALETTES[nextColorIdx % STAGE_PALETTES.length];
    nextColorIdx++;
  }
  return stageColorCache[key];
};

const DENIAL_REASON_LABELS = {
  wrong_diploma: "Wrong diploma",
  wrong_xp: "Wrong experience",
  insufficient_languages: "Not sufficiently languages",
  closed_position: "Job not open anymore",
  other: "Other",
};

const humanizeDenialReason = (reason) =>
  DENIAL_REASON_LABELS[reason] || reason.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

const formatDate = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const CandidateJobApplicationsPanel = ({ candidateId, candidateResumeUrl, onApplied, onCountChange }) => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [expandedDenials, setExpandedDenials] = useState({});

  const fetchApplications = useCallback(() => {
    if (!candidateId) return;
    setLoading(true);
    sh.get(`/internal/candidates/${candidateId}/job_applications`)
      .then((res) => {
        const apps = Array.isArray(res.data) ? res.data : [];
        setApplications(apps);
        onCountChange?.(apps.length);
      })
      .catch(() => setApplications([]))
      .finally(() => setLoading(false));
  }, [candidateId]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const appliedOfferIds = applications.map((a) => a.job_offer_id).filter(Boolean);

  if (loading) {
    return (
      <Box>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <Skeleton variant="rounded" width={130} height={32} sx={{ borderRadius: "0.5rem" }} />
        </Box>
        <Grid container spacing={2}>
          {[...Array(3)].map((_, i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Skeleton variant="rounded" height={110} sx={{ borderRadius: "0.75rem" }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography
          sx={{
            fontSize: 16,
            color: "hsl(150 30% 12%)",
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <WorkIcon sx={{ fontSize: 20, color: "hsl(150 38% 17%)" }} />
          Job Applications
        </Typography>
        <Button
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={() => setApplyOpen(true)}
          size="small"
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13,
            fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
            borderRadius: "0.5rem",
            px: 1.5,
            py: 0.5,
            backgroundColor: "#fff",
            color: "hsl(150 30% 12%)",
            border: "1px solid hsl(140 8% 88%)",
            "&:hover": { backgroundColor: "#fff", opacity: 0.8 },
          }}
        >
          Apply to offer
        </Button>
      </Box>

      {applications.length === 0 ? (
        <Box sx={{ py: 4, textAlign: "center" }}>
          <WorkIcon sx={{ fontSize: 40, color: "hsl(150 10% 70%)", mb: 1 }} />
          <Typography variant="body2" sx={{ color: "hsl(150 10% 46%)" }}>
            No job applications yet.
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {applications.map((app) => {
            const stageStyle = getStageColor(app.stage);
            return (
              <Grid item xs={12} sm={6} key={app.id}>
                <Box
                  onClick={() => navigate(`/internal/job_applications/${app.id}`)}
                  sx={{
                    backgroundColor: "#ffffff",
                    border: "1px solid hsl(140 8% 88%)",
                    borderRadius: "0.75rem",
                    overflow: "hidden",
                    height: "100%",
                    cursor: "pointer",
                    transition: "background-color 150ms ease",
                    "&:hover": { backgroundColor: "hsl(140 10% 97%)" },
                  }}
                >
                  {/* Card header: title + stage chip */}
                  <Box
                    sx={{
                      px: "16px",
                      pt: "12px",
                      pb: "10px",
                      borderBottom: "1px solid hsl(140 8% 88%)",
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 13,
                        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                        color: "hsl(150 30% 12%)",
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      {app.job_offer_title || "Spontaneous application"}
                    </Typography>
                    <Chip
                      label={app.stage || "No stage"}
                      size="small"
                      sx={{
                        backgroundColor: stageStyle.bg,
                        color: stageStyle.color,
                        fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                        fontSize: 11,
                        textTransform: "capitalize",
                        flexShrink: 0,
                      }}
                    />
                  </Box>

                  {/* Card body: company, date, creator, denial reasons */}
                  <Box sx={{ px: "16px", py: "12px" }}>
                    <Stack spacing={0.5}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1 }}>
                        <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
                          {app.company_name && (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <BusinessIcon sx={{ fontSize: 13, color: "hsl(150 10% 46%)" }} />
                              <Typography variant="caption" sx={{ color: "hsl(150 10% 46%)" }}>
                                {app.company_name}
                              </Typography>
                            </Stack>
                          )}
                          <Typography variant="caption" sx={{ color: "hsl(150 10% 46%)" }}>
                            {formatDate(app.created_at)}
                          </Typography>
                        </Stack>
                        {app.created_by && (
                          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
                            <UserAvatar user={app.created_by} size={18} />
                            <Typography variant="caption" sx={{ color: "hsl(150 10% 46%)" }}>
                              {app.created_by.full_name || app.created_by.email}
                            </Typography>
                          </Stack>
                        )}
                      </Box>
                      {app.stage?.toLowerCase() === "closed" && (
                        <Box sx={{ mt: 0.25 }}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.25}
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedDenials((prev) => ({ ...prev, [app.id]: !prev[app.id] }));
                            }}
                            sx={{ cursor: "pointer", userSelect: "none" }}
                          >
                            <ExpandMoreIcon
                              sx={{
                                fontSize: 16,
                                color: "hsl(0 65% 38%)",
                                transition: "transform 0.2s",
                                transform: expandedDenials[app.id] ? "rotate(180deg)" : "rotate(0deg)",
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: 12,
                                color: "hsl(0 65% 38%)",
                                fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
                              }}
                            >
                              Denial reasons
                            </Typography>
                          </Stack>
                          <Collapse in={!!expandedDenials[app.id]}>
                            <Box sx={{ pl: 2.5, pt: 0.5 }}>
                              {app.denial_reasons?.length > 0 ? (
                                app.denial_reasons.map((reason, idx) => (
                                  <Typography
                                    key={idx}
                                    sx={{
                                      fontSize: 12,
                                      color: "hsl(0 50% 40%)",
                                      fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                                      lineHeight: 1.6,
                                    }}
                                  >
                                    • {humanizeDenialReason(reason)}
                                  </Typography>
                                ))
                              ) : (
                                <Typography
                                  sx={{
                                    fontSize: 12,
                                    color: "hsl(0 10% 55%)",
                                    fontFamily: "Segoe UI Regular, sans-serif, system-ui",
                                    fontStyle: "italic",
                                  }}
                                >
                                  No reason provided
                                </Typography>
                              )}
                            </Box>
                          </Collapse>
                        </Box>
                      )}
                    </Stack>
                  </Box>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Apply to offer dialog */}
      <ApplyToOfferDialog
        open={applyOpen}
        onClose={() => setApplyOpen(false)}
        candidateId={candidateId}
        candidateResumeUrl={candidateResumeUrl}
        appliedOfferIds={appliedOfferIds}
        onApplied={() => { fetchApplications(); onApplied?.(); }}
      />
    </Box>
  );
};

export default CandidateJobApplicationsPanel;
